import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MessageSquare, Target, Wand2, Heart, LayoutDashboard,
  Search, Menu, X, Library, FolderOpen, Sparkles, Sun, Moon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { HeaderPoweredByPicker } from "@/components/HeaderPoweredByPicker";
import WikifiMascot from "@/components/WikifiMascot";

/**
 * The single source of truth for the Wikificial top navigation.
 * Pure white 2033-minimalist sticky header — sharp edges, generous spacing.
 * Row 1: brand · My Projects · nav pills · avatar
 * Row 2: large NL-friendly search · Wikifi CTA
 */
export function GlobalHeader({ dense = false, hideSearch = false }) {
  const { user } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const initial = (user?.name?.[0] || user?.email?.[0] || "C").toUpperCase();

  const onSubmit = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) return;
    // Smart routing: if the input reads like a natural-language *question* or
    // intent (has "?", is 5+ words, or starts with a common question word),
    // send it to the Wikifi workspace so the selected frontier model can
    // answer it — instead of scanning it against tool names in the directory.
    // Short keyword-style queries stay in the directory search.
    const isQuestion =
      v.includes("?") ||
      v.split(/\s+/).length >= 5 ||
      /^(what|how|why|can|when|where|who|which|is|are|do|does|should|would|could|help|i\s+(am|want|need|would|would\s+like|'m)|looking|create|build|make|find|show|explain|compare|recommend)\b/i.test(v);
    if (isQuestion) {
      nav(`/wikifi?q=${encodeURIComponent(v)}`);
    } else {
      nav(`/?search=${encodeURIComponent(v)}`);
    }
  };

  const isActive = (path) => loc.pathname === path;
  const pillBase = "h-8 px-3.5 rounded-md text-[12.5px] font-semibold inline-flex items-center gap-1.5 border transition-all whitespace-nowrap";
  const pill = (active) => `${pillBase} ${active
      ? "bg-white text-[#0B1220] border-white"
      : "bg-transparent text-white/85 border-white/20 hover:border-white/60"}`;

  return (
    <header
      className="sticky top-0 z-40 w-full bg-[#0B1220] text-white border-b border-white/10"
      data-testid="global-header"
    >
      {/* ── Row 1: brand + primary nav ─────────────────────────── */}
      <div className="max-w-[1920px] mx-auto w-full flex items-center h-[54px] px-4 sm:px-6 lg:px-8 gap-3">
        <button
          className="md:hidden -ml-1 p-2 rounded-md hover:bg-white/10"
          onClick={() => setMobileOpen(v => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        <Link to="/" className="relative inline-flex items-center pr-2" data-testid="brand-mark">
          <img
            src="/brand/wikificial-lockup-white.png"
            alt="Wikificial"
            // Outlined lockup (~4.85:1 aspect). 30px height keeps the
            // hairline strokes readable on the dark navy header without
            // overpowering the pill nav on its right.
            style={{ height: 30, width: "auto", display: "block" }}
            className="select-none"
            draggable={false}
          />
        </Link>

        {/* Model picker — icon + name + chevron, no label. */}
        <div className="ml-2 hidden md:block">
          <HeaderPoweredByPicker />
        </div>

        {/* Primary top nav — icon + label so it's discoverable at a glance. */}
        <div className="hidden md:flex items-center gap-1 ml-2">
          {[
            { to: "/wikifi",      icon: "brand-W",             label: "Wikifi",     testId: "nav-wikifi-workspace" },
            { to: "/algorithm",   icon: "fa-code-branch",       label: "Algorithms", testId: "nav-algorithm" },
            { to: "/prompts",     icon: "fa-book-open",         label: "Prompts",    testId: "nav-prompts" },
            { to: "/pricing",     icon: "fa-tag",               label: "Pricing",    testId: "nav-pricing" },
          ].map(n => {
            const active = isActive(n.to);
            const isBrandW = n.icon === "brand-W";
            return (
              <Link
                key={n.to}
                to={n.to}
                title={n.label}
                data-testid={n.testId}
                className={`h-8 pl-1.5 pr-2.5 rounded-md inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${
                  active
                    ? "bg-white text-[#0B1220]"
                    : "text-white hover:bg-white/10"
                }`}
              >
                <span className="w-5 h-5 inline-flex items-center justify-center flex-shrink-0">
                  {isBrandW ? (
                    <span
                      className="font-display leading-none"
                      style={{ fontSize: 13, fontWeight: 700 }}
                      aria-hidden="true"
                    >
                      W
                    </span>
                  ) : (
                    <i className={`fa-solid ${n.icon} text-[12px]`} aria-hidden="true"/>
                  )}
                </span>
                <span>{n.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Right actions: Create · My Stack · Dashboard (icon-only, FA) */}
        <div className="ml-auto flex items-center gap-1.5">
          {[
            { to: "/create",    icon: "fa-plus",       title: "Create",    testId: "nav-create" },
            { to: "/favorites", icon: "fa-bookmark",   title: "My Stack",  testId: "nav-mystack" },
            { to: "/dashboard", icon: "fa-gauge-high", title: "Dashboard", testId: "nav-dashboard" },
            ...(user?.is_admin ? [{ to: "/admin", icon: "fa-shield-halved", title: "Admin", testId: "nav-admin" }] : []),
          ].map(a => (
            <Link
              key={a.to}
              to={a.to}
              title={a.title}
              data-testid={a.testId}
              className="rounded-md inline-flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              style={{ width: 34, height: 34 }}
            >
              <i className={`fa-solid ${a.icon} text-[13.5px]`} aria-hidden="true"/>
            </Link>
          ))}
          {user ? (
            user.picture ? (
              <Link
                to="/profile"
                className="w-9 h-9 rounded-full ring-2 ring-white/20 shadow-sm overflow-hidden hover:ring-[#F97316]/60 transition-all ml-1"
                title={user.email}
                data-testid="nav-avatar"
              >
                <img src={user.picture} alt={user.name || user.email} className="w-full h-full object-cover"/>
              </Link>
            ) : (
              <Link
                to="/profile"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/20 text-white flex items-center justify-center font-bold text-[13px] shadow-sm hover:border-white/60 transition-colors ml-1"
                title={user.email}
                data-testid="nav-avatar"
              >
                {initial}
              </Link>
            )
          ) : (
            <Link
              to="/login"
              className={`${pillBase} bg-transparent text-white border-white/40 hover:border-white ml-1`}
              data-testid="nav-signin"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>

      {/* ── Row 2: natural-language search + Wikifi CTA ───────── */}
      {!dense && !hideSearch && (
        <div className="max-w-[1920px] mx-auto w-full flex items-center h-[52px] px-4 sm:px-6 lg:px-8 gap-3 border-t border-white/10 bg-[#0B1220]">
          <form onSubmit={onSubmit} className="flex-1 flex items-center gap-2">
            <div className="relative flex-1 max-w-3xl">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="I'm looking for an AI that can help me do budgeting…"
                className="w-full h-9 rounded-md bg-white/5 border border-white/15 pl-11 pr-4 text-[13px] text-white placeholder:text-white/60 focus:outline-none focus:border-white/60 transition-all"
                data-testid="header-search"
              />
            </div>
            <button
              type="submit"
              className="h-9 px-4 rounded-md bg-white text-[#0B1220] text-[12.5px] font-bold flex items-center gap-1.5 hover:brightness-90 transition-all whitespace-nowrap"
              data-testid="header-search-btn"
            >
              <Search size={12} /> Search
            </button>
          </form>
        </div>
      )}

      {/* ── Mobile drawer ──────────────────────────────────────── */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0B1220] px-4 py-3 space-y-1.5" data-testid="mobile-drawer">
          {[
            ["/wikifi", "Wikifi workspace", Sparkles],
            ["/prompts", "Prompts", Library],
            ["/chat", "New Chat", MessageSquare],
            ["/algorithm", "Algorithm", Target],
            ["/create", "Create", Wand2],
            ["/favorites", "My Stack", Heart],
            ["/dashboard", "Dashboard", LayoutDashboard],
            ["/submit", "Submit Tool", FolderOpen],
            ...(user?.is_admin ? [["/admin", "Admin", Sun]] : []),
          ].map(([to, label, Icon]) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/10 text-[14px] font-medium text-white"
            >
              <Icon size={14} /> {label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
